import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import PushPinIcon from '@mui/icons-material/PushPin';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import CollectionsIcon from '@mui/icons-material/Collections';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';
import {
  MITRA_CARD_ASPECT,
  MITRA_CARD_HEIGHT,
  MITRA_CARD_MAX_INPUT_BYTES,
  MITRA_CARD_TARGET_BYTES,
  MITRA_CARD_WIDTH,
  compressMitraCardImageForUpload,
  toOptimizedMitraCardDeliveryUrl,
} from '../../utils/compressImageUpload';
import {
  filterCoverCandidates,
  getPreviewCarouselCards,
  listCoverPickCandidates,
  resolveSectionCoverUrl,
} from './citizenAppSectionMedia';
import ShellChromeBoard from '../../components/ShellChromeBoard';
import CitizenFullScreenBoard from '../../components/CitizenFullScreenBoard';

const api = axios.create({
  baseURL: `${getApiBaseUrl()}/citizen-home-controls`,
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const chromeApi = axios.create({
  baseURL: `${getApiBaseUrl()}/app-shell-chrome`,
  timeout: 25000,
});

chromeApi.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['Content-Type'] = 'application/json';
  return config;
});

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';

const BADGE_PRESETS = ['New', 'Hot', 'Season', 'Today', 'Must see', 'Local', 'Festive'];

const HIGH_TRAFFIC_KEYS = new Set([
  'digital_pamphlets',
  'food_zone',
  'fresh_market',
  'street_vendors',
  'jobs',
  'learn_zone',
  'citizen_posts',
]);

const SECTION_HINTS = {
  digital_pamphlets: 'Business brochures — revenue surface',
  food_zone: 'Food & order intent — peak evening traffic',
  fresh_market: 'Daily essentials — morning habit loop',
  street_vendors: 'Live mobile carts — call or send pin (Street Vendor Nearby)',
  health_updates: 'Trust & utility — doctors/camps',
  local_2nd_market: 'Marketplace discovery',
  real_estate: 'High-ticket consideration',
  jobs: 'Career Zone — retention & local hire',
  learn_zone: 'Learn Zone — Online Courses & Campus Classes (KNLS + approved Local Tutors)',
  rentals: 'Need-based search',
  religious: 'Community calendar',
  govt_alerts: 'Civic utility — keep reachable',
  movies_events: 'Weekend discovery',
  citizen_posts: 'UGC listings & requirements',
  various_category_updates: 'Cross-category local business updates',
};

/** Merchandising layouts — reorder existing keys; unknown keys stay at end. */
const LAYOUT_PRESETS = {
  essentials_first: {
    label: 'Essentials first',
    blurb: 'Food → Market → Pamphlets → Career — habit loop for daily opens.',
    keys: [
      'food_zone',
      'fresh_market',
      'street_vendors',
      'digital_pamphlets',
      'jobs',
      'learn_zone',
      'citizen_posts',
      'health_updates',
      'govt_alerts',
      'local_2nd_market',
      'rentals',
      'real_estate',
      'religious',
      'movies_events',
    ],
  },
  discovery: {
    label: 'Discovery mode',
    blurb: 'Lead with pamphlets & UGC, push commerce lower for browse sessions.',
    keys: [
      'digital_pamphlets',
      'citizen_posts',
      'movies_events',
      'local_2nd_market',
      'food_zone',
      'fresh_market',
      'jobs',
      'learn_zone',
      'real_estate',
      'rentals',
      'health_updates',
      'religious',
      'govt_alerts',
    ],
  },
  festival: {
    label: 'Festival spotlight',
    blurb: 'Events + food + faith up; hide low-season commerce via visibility separately.',
    keys: [
      'movies_events',
      'food_zone',
      'religious',
      'digital_pamphlets',
      'fresh_market',
      'citizen_posts',
      'jobs',
      'health_updates',
      'govt_alerts',
      'local_2nd_market',
      'rentals',
      'real_estate',
    ],
  },
};

const toDatetimeLocalValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocalValue = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const buildCitizenSectionPrompt = ({ title, subtitle, badge, aiBrief, sectionKey }) => {
  const hint = SECTION_HINTS[sectionKey] || 'Local town lifestyle category';
  return `Create ONE landscape cover thumbnail for a LOCSY Citizen home carousel section.

Section key: ${String(sectionKey || '').trim() || '(unnamed)'}
Section title: ${String(title || '').trim() || '(unnamed)'}
Tagline: ${String(subtitle || '').trim() || 'N/A'}
Badge pill (optional, do NOT render as text in image): ${String(badge || '').trim() || 'none'}
Marketing brief: ${String(aiBrief || '').trim() || hint}

STRICT OUTPUT (carousel cover art only):
- Exact canvas: ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} pixels (3:2 landscape — NOT square, NOT 512×512 catalog product size)
- Style: warm, inviting Indian small-town lifestyle illustration / premium product marketing tile
- Subject: single clear metaphor for this home section (food, fresh produce, jobs, pamphlets, etc.) with breathing room
- Background: soft gradient or solid that reads well at ~160px card width on phone
- NO readable text, logos, watermarks, UI chrome, phone frames, or badge stickers in the image
- File size target: under 200 KB (web). Prefer clean shapes over noisy photos
- Output ONLY this one ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} image`;
};

const uploadCitizenSectionThumbnail = async (file) => {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary is not configured.');
  }
  if (file.size > MITRA_CARD_MAX_INPUT_BYTES) {
    throw new Error('Image too large (max 12 MB).');
  }
  const prepared = await compressMitraCardImageForUpload(file);
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'citizen_screen_sections');
  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }
  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );
  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) throw new Error('Thumbnail upload failed');
  return toOptimizedMitraCardDeliveryUrl(secureUrl);
};

const emptyForm = () => ({
  title: '',
  subtitle: '',
  badge: '',
  thumbnail_url: '',
  ai_brief: '',
  admin_notes: '',
  is_visible: true,
  is_spotlight: false,
  visible_from: '',
  visible_until: '',
});

const SectionThumb = ({ section, sx }) => {
  const resolved = resolveSectionCoverUrl(section);
  const src = resolved.url;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: '100%', color: 'grey.400', px: 2, ...sx }}
      >
        <Typography variant="caption" textAlign="center">
          No cover yet
          <br />
          {section?.section_key || ''}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...sx }}
    />
  );
};

const CoverSourceChip = ({ section }) => {
  const { source } = resolveSectionCoverUrl(section);
  if (source === 'cms') return null;
  if (source === 'app') {
    return (
      <Chip
        label="App default"
        size="small"
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          bgcolor: 'rgba(15,23,42,0.75)',
          color: 'white',
        }}
      />
    );
  }
  if (source === 'demo') {
    return (
      <Chip
        label="App demo"
        size="small"
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          bgcolor: 'rgba(15,23,42,0.75)',
          color: 'white',
        }}
      />
    );
  }
  return (
    <Chip
      label="No cover"
      size="small"
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        bgcolor: 'rgba(15,23,42,0.75)',
        color: 'white',
      }}
    />
  );
};

const HomeFeedPreview = ({ sections }) => (
  <Box
    sx={{
      mx: 'auto',
      width: '100%',
      maxWidth: 320,
      borderRadius: 4,
      overflow: 'hidden',
      bgcolor: '#F8FAFC',
      border: '8px solid #0F172A',
      boxShadow: '0 12px 40px rgba(15,23,42,0.35)',
    }}
  >
    <Box sx={{ bgcolor: '#0F172A', px: 1.5, py: 1 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
        Locsy · Home
      </Typography>
      <Typography variant="subtitle2" fontWeight={800} color="white">
        Citizen feed
      </Typography>
    </Box>
    <Stack
      spacing={1.75}
      sx={{
        maxHeight: 420,
        overflow: 'auto',
        px: 1.25,
        py: 1.5,
        bgcolor: '#FFFFFF',
      }}
    >
      {sections.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No visible carousels
        </Typography>
      ) : (
        sections.map((s) => {
          const cards = getPreviewCarouselCards(s);
          return (
            <Box key={s.id}>
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{ color: '#111111', fontSize: 13, lineHeight: 1.2 }}
                  noWrap
                >
                  {s.title}
                </Typography>
                {s.badge ? (
                  <Chip
                    label={s.badge}
                    size="small"
                    sx={{ height: 18, fontSize: 10, bgcolor: '#DCFCE7', color: '#166534' }}
                  />
                ) : null}
                {s.is_spotlight ? (
                  <Chip
                    label="Spotlight"
                    size="small"
                    sx={{ height: 18, fontSize: 10 }}
                    color="success"
                  />
                ) : null}
              </Stack>
              {s.subtitle ? (
                <Typography
                  variant="caption"
                  sx={{ color: '#6B7280', display: 'block', mb: 0.75, lineHeight: 1.2 }}
                  noWrap
                >
                  {s.subtitle}
                </Typography>
              ) : null}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  overflowX: 'auto',
                  pb: 0.5,
                  '&::-webkit-scrollbar': { height: 4 },
                }}
              >
                {cards.map((card, idx) => (
                  <Box
                    key={`${s.id}-${idx}`}
                    sx={{
                      flex: '0 0 auto',
                      width: 108,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      bgcolor: '#F1F5F9',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Box
                      component="img"
                      src={card.image}
                      alt=""
                      sx={{
                        width: '100%',
                        height: 64,
                        objectFit: 'cover',
                        display: 'block',
                        bgcolor: '#0F172A',
                      }}
                      onError={(e) => {
                        e.currentTarget.style.visibility = 'hidden';
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        px: 0.75,
                        py: 0.5,
                        fontWeight: 700,
                        fontSize: 10,
                        lineHeight: 1.25,
                        color: '#111',
                        height: 34,
                        overflow: 'hidden',
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })
      )}
    </Stack>
  </Box>
);

const CoverPickerDialog = ({ open, onClose, sections, preferSectionKey, onPick }) => {
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  useEffect(() => {
    if (open) {
      setQuery(preferSectionKey ? String(preferSectionKey) : '');
      setSourceFilter('all');
    }
  }, [open, preferSectionKey]);

  const all = useMemo(() => listCoverPickCandidates(sections), [sections]);

  const filtered = useMemo(() => {
    let list = filterCoverCandidates(all, query);
    if (sourceFilter !== 'all') {
      list = list.filter((c) => c.source === sourceFilter);
    }
    // Prefer matches for the section being edited at the top when query empty/matches key
    if (preferSectionKey) {
      list = [...list].sort((a, b) => {
        const aHit = a.sectionKey === preferSectionKey ? 0 : 1;
        const bHit = b.sectionKey === preferSectionKey ? 0 : 1;
        return aHit - bHit;
      });
    }
    return list;
  }, [all, query, sourceFilter, preferSectionKey]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Search & pick cover
        <Typography variant="caption" display="block" color="text.secondary">
          Retrieve Locsy app demos, section defaults, or existing CMS covers — click to use.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="Search food, jobs, biryani, section key…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <MenuItem value="all">All sources</MenuItem>
              <MenuItem value="demo">App demos</MenuItem>
              <MenuItem value="app">App defaults</MenuItem>
              <MenuItem value="cms">CMS covers</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {filtered.length} image{filtered.length === 1 ? '' : 's'}
        </Typography>
        {filtered.length === 0 ? (
          <Alert severity="info">No covers match that search.</Alert>
        ) : (
          <Grid container spacing={1.5}>
            {filtered.map((item) => (
              <Grid item xs={6} sm={4} md={3} key={item.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s, border-color 0.15s',
                    '&:hover': { boxShadow: 4, borderColor: 'primary.main' },
                    '&:focus-within': { borderColor: 'primary.main', boxShadow: 3 },
                  }}
                  onClick={() => onPick(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick(item);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Pick ${item.title}`}
                >
                  <Box sx={{ bgcolor: '#0F172A', aspectRatio: '3 / 2' }}>
                    <Box
                      component="img"
                      src={item.url}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => {
                        e.currentTarget.style.opacity = 0.2;
                      }}
                    />
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <Typography variant="caption" fontWeight={700} display="block" noWrap>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {item.label}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const SortableSectionCard = ({
  section,
  index,
  total,
  dragDisabled,
  onMove,
  onToggleVisible,
  onSpotlight,
  onEdit,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    disabled: dragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : section.is_visible ? 1 : 0.55,
    zIndex: isDragging ? 2 : 1,
  };

  return (
    <Grid item xs={12}>
      <Box ref={setNodeRef} style={style} sx={{ height: '100%' }}>
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          borderColor: section.is_spotlight
            ? 'success.main'
            : isDragging
              ? 'primary.main'
              : 'divider',
          boxShadow: isDragging ? 6 : 0,
          outline: isDragging ? '2px solid' : 'none',
          outlineColor: 'primary.main',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: '#0F172A',
            flexShrink: 0,
            width: { xs: '100%', sm: 220 },
            minHeight: { xs: 0, sm: 140 },
            aspectRatio: { xs: '3 / 2', sm: 'auto' },
          }}
        >
          <SectionThumb section={section} />
          <Chip
            label={`#${index + 1}`}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              height: 32,
              minWidth: 44,
              bgcolor: '#FBBF24',
              color: '#0F172A',
              fontWeight: 900,
              fontSize: '0.95rem',
              letterSpacing: 0.2,
              border: '2px solid #fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
              '& .MuiChip-label': { px: 1.25 },
            }}
          />
          <CoverSourceChip section={section} />
          {section.is_spotlight ? (
            <Chip
              icon={<PushPinIcon sx={{ color: 'white !important', fontSize: 14 }} />}
              label="Spotlight"
              size="small"
              color="success"
              sx={{ position: 'absolute', top: 8, right: 8 }}
            />
          ) : null}
          <Tooltip title={dragDisabled ? 'Clear search to drag-reorder' : 'Drag to reorder · focus handle & use ↑/↓ arrows'}>
            <span>
              <IconButton
                size="small"
                {...attributes}
                {...listeners}
                disabled={dragDisabled}
                aria-label={`Drag reorder ${section.title}`}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  cursor: dragDisabled ? 'not-allowed' : 'grab',
                  '&:active': { cursor: dragDisabled ? 'not-allowed' : 'grabbing' },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                  },
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <CardContent sx={{ pb: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box minWidth={0}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {section.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {section.section_key}
              </Typography>
              {section.subtitle ? (
                <Typography variant="body2" color="text.secondary" mt={0.5} noWrap>
                  {section.subtitle}
                </Typography>
              ) : null}
              {SECTION_HINTS[section.section_key] ? (
                <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                  {SECTION_HINTS[section.section_key]}
                </Typography>
              ) : null}
            </Box>
            {section.badge ? <Chip label={section.badge} size="small" /> : null}
          </Stack>

          <Stack direction="row" spacing={0.5} mt={1.5} flexWrap="wrap" useFlexGap>
            <Tooltip title="Move up (also: focus drag handle → ↑)">
              <span>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => onMove(section.id, -1)}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down (also: focus drag handle → ↓)">
              <span>
                <IconButton
                  size="small"
                  disabled={index >= total - 1}
                  onClick={() => onMove(section.id, 1)}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={section.is_visible ? 'Hide from home (saves now)' : 'Show on home (saves now)'}>
              <IconButton size="small" onClick={() => onToggleVisible(section.id)}>
                {section.is_visible ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Spotlight (floats to top without losing order)">
              <IconButton
                size="small"
                color={section.is_spotlight ? 'success' : 'default'}
                onClick={() => onSpotlight(section.id)}
              >
                <PushPinIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit merchandising">
              <IconButton size="small" onClick={() => onEdit(section)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
      </Box>
    </Grid>
  );
};

const CitizenScreens = () => {
  const [pageTab, setPageTab] = useState('fullscreen'); // fullscreen | home | bottom_nav | top_bar
  const [pageBlocks, setPageBlocks] = useState([]);
  const [blocksDirty, setBlocksDirty] = useState(false);
  const [blocksSaving, setBlocksSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [cmsReady, setCmsReady] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [aiPrompt, setAiPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [importingThumbs, setImportingThumbs] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const [jumpId, setJumpId] = useState('');
  const [jumpPos, setJumpPos] = useState(1);
  const [activeDragId, setActiveDragId] = useState(null);
  const [pageSearch, setPageSearch] = useState('');
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [chrome, setChrome] = useState({ bottom_nav: [], top_bar: [] });
  const [chromeCatalog, setChromeCatalog] = useState({ bottom_nav: [], top_bar: [] });
  const [chromeDirty, setChromeDirty] = useState(false);
  const [chromeSaving, setChromeSaving] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const thumbInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const applyChromePayload = (data, meta) => {
    setChrome({
      bottom_nav: data?.bottom_nav || [],
      top_bar: data?.top_bar || [],
    });
    const cat = meta?.catalog;
    setChromeCatalog({
      bottom_nav: (cat?.bottom_nav || []).map((x) => x.item_key || x),
      top_bar: (cat?.top_bar || []).map((x) => x.item_key || x),
    });
    setChromeDirty(false);
  };

  const loadChrome = useCallback(async () => {
    try {
      const res = await chromeApi.get('/citizen');
      if (!res.data?.success) return;
      applyChromePayload(res.data.data, res.data.meta);
    } catch (e) {
      console.warn('Citizen shell chrome load failed', e);
    }
  }, []);

  const loadPageBlocks = useCallback(async () => {
    try {
      const res = await api.get('/page-blocks');
      if (!res.data?.success) return;
      setPageBlocks(res.data.data?.blocks || []);
      setBlocksDirty(false);
    } catch (e) {
      console.warn('Citizen page blocks load failed', e);
      setPageBlocks([
        { block_key: 'search_row', label_en: 'Search row', is_visible: true, sort_order: 0 },
        { block_key: 'home_feed', label_en: 'Home feed carousels', is_visible: true, sort_order: 10 },
      ]);
    }
  }, []);

  const savePageBlocks = async () => {
    setBlocksSaving(true);
    try {
      const res = await api.put('/page-blocks', { blocks: pageBlocks });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Blocks save failed' });
        return;
      }
      setPageBlocks(res.data.data?.blocks || pageBlocks);
      setBlocksDirty(false);
      setSnack({
        severity: 'success',
        message: 'Citizen full screen blocks saved — app refreshes on next open.',
      });
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Blocks save failed',
      });
    } finally {
      setBlocksSaving(false);
    }
  };

  const loadSections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/');
      if (!res.data?.success) {
        setError(res.data?.message || 'Failed to load Citizen screens');
        setSections([]);
        return;
      }
      const rows = (res.data.data || []).map((r, idx) => ({
        ...r,
        sort_order: Number.isFinite(Number(r.sort_order)) ? Number(r.sort_order) : idx,
      }));
      rows.sort((a, b) => a.sort_order - b.sort_order);
      setSections(rows);
      setCmsReady(res.data.meta?.cmsReady !== false);
      setOrderDirty(false);
      await Promise.all([loadChrome(), loadPageBlocks()]);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [loadChrome, loadPageBlocks]);

  const saveChrome = async () => {
    setChromeSaving(true);
    try {
      const res = await chromeApi.put('/citizen', {
        bottom_nav: chrome.bottom_nav,
        top_bar: chrome.top_bar,
      });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Chrome save failed' });
        return;
      }
      applyChromePayload(res.data.data, res.data.meta);
      setSnack({
        severity: 'success',
        message:
          'Nav / top bar saved. Citizen updates on next refresh — API must match the Citizen app.',
      });
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Chrome save failed',
      });
    } finally {
      setChromeSaving(false);
    }
  };

  /** Sync shell chrome + page blocks catalog + reload home CMS (Citizen ↔ React). */
  const syncAllWithCitizen = useCallback(async () => {
    setSyncingAll(true);
    try {
      const [chromeRes, blocksRes] = await Promise.all([
        chromeApi.post('/citizen/sync-catalog').catch((e) => ({
          data: { success: false, message: e.response?.data?.message || e.message },
        })),
        api.post('/page-blocks/sync').catch((e) => ({
          data: { success: false, message: e.response?.data?.message || e.message },
        })),
      ]);
      if (chromeRes.data?.success) {
        applyChromePayload(chromeRes.data.data, chromeRes.data.meta);
      }
      if (blocksRes.data?.success) {
        setPageBlocks(blocksRes.data.data?.blocks || []);
        setBlocksDirty(false);
      }
      await loadSections();
      const chromeN = chromeRes.data?.meta?.inserted?.length || 0;
      const blocksN = blocksRes.data?.meta?.inserted?.length || 0;
      setSnack({
        severity: 'success',
        message: `Synced all controls ↔ Citizen (nav/top +${chromeN}, blocks +${blocksN}). App refreshes on next open.`,
      });
    } catch (e) {
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Sync failed',
      });
    } finally {
      setSyncingAll(false);
    }
  }, [loadSections]);

  const didAutoPullCovers = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadSections();
      if (cancelled || didAutoPullCovers.current) return;
      didAutoPullCovers.current = true;
      // After first load, pull empty CMS covers from Citizen app defaults (quiet).
      try {
        const res = await api.get('/');
        if (!res.data?.success || res.data.meta?.cmsReady === false) return;
        const rows = res.data.data || [];
        const covers = rows
          .filter((s) => !String(s.thumbnail_url || '').trim())
          .map((s) => {
            const { url, source } = resolveSectionCoverUrl(s);
            if (!url || source === 'none') return null;
            return { id: s.id, thumbnail_url: url };
          })
          .filter(Boolean);
        if (covers.length === 0) return;
        const imported = await api.post('/import-app-thumbnails', { covers });
        if (imported.data?.success && (imported.data.meta?.updated || 0) > 0) {
          await loadSections();
        }
      } catch (_) {
        /* fail-open — manual Sync still available */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSections]);

  const sorted = useMemo(
    () => [...sections].sort((a, b) => a.sort_order - b.sort_order),
    [sections],
  );

  const pageQuery = String(pageSearch || '').trim().toLowerCase();
  const searchActive = pageQuery.length > 0;

  const filteredSorted = useMemo(() => {
    if (!searchActive) return sorted;
    return sorted.filter((s) => {
      const hay = [s.title, s.subtitle, s.section_key, s.badge, SECTION_HINTS[s.section_key]]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return hay.includes(pageQuery);
    });
  }, [sorted, searchActive, pageQuery]);

  const displayList = searchActive ? filteredSorted : sorted;

  const visibleSorted = useMemo(
    () =>
      sorted
        .filter((s) => s.is_visible)
        .sort((a, b) => {
          if (a.is_spotlight && !b.is_spotlight) return -1;
          if (!a.is_spotlight && b.is_spotlight) return 1;
          return a.sort_order - b.sort_order;
        }),
    [sorted],
  );

  const stats = useMemo(() => {
    const visible = sorted.filter((s) => s.is_visible).length;
    const hidden = sorted.length - visible;
    const scheduled = sorted.filter((s) => s.visible_from || s.visible_until).length;
    const spotlight = sorted.find((s) => s.is_spotlight);
    const missingCms = sorted.filter((s) => !String(s.thumbnail_url || '').trim()).length;
    return { visible, hidden, scheduled, spotlight, missingCms };
  }, [sorted]);

  const activeDragSection = useMemo(
    () => sorted.find((s) => s.id === activeDragId) || null,
    [sorted, activeDragId],
  );

  const renumber = (list) =>
    list.map((s, i) => ({
      ...s,
      sort_order: i,
    }));

  const openEdit = (section) => {
    setEditing(section);
    setForm({
      title: section.title || '',
      subtitle: section.subtitle || '',
      badge: section.badge || '',
      thumbnail_url: section.thumbnail_url || '',
      ai_brief: section.ai_brief || '',
      admin_notes: section.admin_notes || '',
      is_visible: section.is_visible !== false,
      is_spotlight: section.is_spotlight === true,
      visible_from: toDatetimeLocalValue(section.visible_from),
      visible_until: toDatetimeLocalValue(section.visible_until),
    });
    setAiPrompt('');
    setDialogOpen(true);
  };

  const handleGeneratePrompt = () => {
    if (!String(form.title || '').trim()) {
      setSnack({ severity: 'warning', message: 'Enter title first (used in AI prompt).' });
      return;
    }
    setAiPrompt(
      buildCitizenSectionPrompt({
        title: form.title,
        subtitle: form.subtitle,
        badge: form.badge,
        aiBrief: form.ai_brief,
        sectionKey: editing?.section_key,
      }),
    );
  };

  const handleCopyPrompt = async () => {
    if (!aiPrompt) {
      setSnack({ severity: 'warning', message: 'Generate a prompt first.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(aiPrompt);
      setSnack({ severity: 'success', message: 'Prompt copied — paste in Gemini / ChatGPT.' });
    } catch {
      setSnack({ severity: 'error', message: 'Copy failed' });
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    try {
      const url = await uploadCitizenSectionThumbnail(file);
      setForm((f) => ({ ...f, thumbnail_url: url }));
      setSnack({
        severity: 'success',
        message: `Uploaded & auto-cropped to ${MITRA_CARD_WIDTH}×${MITRA_CARD_HEIGHT} (${MITRA_CARD_ASPECT}).`,
      });
    } catch (e) {
      setSnack({ severity: 'error', message: e.message || 'Upload failed' });
    } finally {
      setThumbUploading(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!editing?.id) return;
    if (!String(form.title || '').trim()) {
      setSnack({ severity: 'warning', message: 'Title is required.' });
      return;
    }
    if (
      form.is_visible === false &&
      HIGH_TRAFFIC_KEYS.has(editing.section_key) &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Hide “${editing.title || editing.section_key}”? This removes it from Citizen home until you turn it back on.`,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        badge: form.badge,
        thumbnail_url: form.thumbnail_url,
        ai_brief: form.ai_brief,
        admin_notes: form.admin_notes,
        is_visible: form.is_visible,
        is_spotlight: form.is_spotlight,
        visible_from: fromDatetimeLocalValue(form.visible_from),
        visible_until: fromDatetimeLocalValue(form.visible_until),
      };
      const res = await api.patch(`/${encodeURIComponent(editing.id)}`, payload);
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Save failed' });
        return;
      }
      setSnack({
        severity: 'success',
        message: 'Section updated — Citizen app refreshes home ~every 30s.',
      });
      setDialogOpen(false);
      await loadSections();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleResetThumb = async () => {
    if (!editing?.id) return;
    setSaving(true);
    try {
      const res = await api.post(`/${encodeURIComponent(editing.id)}/reset-thumbnail`);
      if (res.data?.success) {
        setForm((f) => ({ ...f, thumbnail_url: '' }));
        setSnack({ severity: 'success', message: 'Cover cleared — demos / live posts use their own art.' });
        await loadSections();
      }
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  const importAppThumbnails = async () => {
    if (!cmsReady) {
      setSnack({
        severity: 'warning',
        message: 'CMS migration required before saving covers.',
      });
      return;
    }
    const covers = sorted
      .filter((s) => !String(s.thumbnail_url || '').trim())
      .map((s) => {
        const { url, source } = resolveSectionCoverUrl(s);
        if (!url || source === 'none') return null;
        return { id: s.id, thumbnail_url: url };
      })
      .filter(Boolean);

    if (covers.length === 0) {
      setSnack({ severity: 'info', message: 'All sections already have CMS covers.' });
      return;
    }

    setImportingThumbs(true);
    try {
      const res = await api.post('/import-app-thumbnails', { covers });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Import failed' });
        return;
      }
      const n = res.data.meta?.updated ?? covers.length;
      setSnack({
        severity: 'success',
        message: `Saved ${n} cover${n === 1 ? '' : 's'} from Locsy app defaults / demos.`,
      });
      await loadSections();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setImportingThumbs(false);
    }
  };

  const toggleVisible = async (id) => {
    const row = sections.find((s) => s.id === id);
    if (!row) return;
    if (
      row?.is_visible &&
      HIGH_TRAFFIC_KEYS.has(row.section_key) &&
      // eslint-disable-next-line no-alert
      !window.confirm(`Hide “${row.title}” from Citizen home?`)
    ) {
      return;
    }
    const nextVisible = !row.is_visible;
    // Optimistic UI — hide/show takes effect immediately for the admin.
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, is_visible: nextVisible } : s)),
    );
    try {
      const res = await api.patch(`/${id}`, { is_visible: nextVisible });
      if (!res.data?.success) {
        // Roll back if API rejected the change.
        setSections((prev) =>
          prev.map((s) => (s.id === id ? { ...s, is_visible: row.is_visible } : s)),
        );
        setSnack({
          severity: 'error',
          message: res.data?.message || 'Failed to update visibility',
        });
        return;
      }
      setSnack({
        severity: 'success',
        message: nextVisible
          ? `“${row.title}” is visible on Citizen home.`
          : `“${row.title}” hidden from Citizen home.`,
      });
    } catch (e) {
      setSections((prev) =>
        prev.map((s) => (s.id === id ? { ...s, is_visible: row.is_visible } : s)),
      );
      setSnack({
        severity: 'error',
        message: e.response?.data?.message || e.message || 'Failed to update visibility',
      });
    }
  };

  const setSpotlight = (id) => {
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        is_spotlight: s.id === id ? !s.is_spotlight : false,
      })),
    );
    setOrderDirty(true);
  };

  const moveSection = (id, dir) => {
    const list = renumber(sorted);
    const idx = list.findIndex((s) => s.id === id);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= list.length) return;
    const next = [...list];
    const tmp = next[idx];
    next[idx] = next[swapWith];
    next[swapWith] = tmp;
    setSections(renumber(next));
    setOrderDirty(true);
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || active.id === over.id) return;
    const list = renumber(sorted);
    const oldIndex = list.findIndex((s) => s.id === active.id);
    const newIndex = list.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setSections(renumber(arrayMove(list, oldIndex, newIndex)));
    setOrderDirty(true);
  };

  const handleDragCancel = () => setActiveDragId(null);

  const jumpToPosition = () => {
    if (!jumpId) {
      setSnack({ severity: 'warning', message: 'Pick a carousel section first.' });
      return;
    }
    const list = renumber(sorted);
    const from = list.findIndex((s) => s.id === jumpId);
    if (from < 0) return;
    const target = Math.max(0, Math.min(list.length - 1, Number(jumpPos) - 1));
    if (from === target) {
      setSnack({ severity: 'info', message: 'Already at that position.' });
      return;
    }
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(target, 0, item);
    setSections(renumber(next));
    setOrderDirty(true);
    setSnack({
      severity: 'success',
      message: `Moved “${item.title}” to position ${target + 1}. Click Save order.`,
    });
  };

  const applyPreset = (presetKey) => {
    const preset = LAYOUT_PRESETS[presetKey];
    if (!preset) return;
    const byKey = new Map(sorted.map((s) => [s.section_key, s]));
    const ordered = [];
    preset.keys.forEach((k) => {
      if (byKey.has(k)) {
        ordered.push(byKey.get(k));
        byKey.delete(k);
      }
    });
    byKey.forEach((s) => ordered.push(s));
    setSections(renumber(ordered));
    setOrderDirty(true);
    setSnack({
      severity: 'success',
      message: `Applied “${preset.label}”. Review preview, then Save order.`,
    });
  };

  const saveOrderVisibility = async () => {
    setBulkSaving(true);
    try {
      const payload = renumber(sorted).map((s) => ({
        id: s.id,
        is_visible: s.is_visible,
        sort_order: s.sort_order,
        is_spotlight: s.is_spotlight === true,
      }));
      const res = await api.put('/bulk', { sections: payload });
      if (!res.data?.success) {
        setSnack({ severity: 'error', message: res.data?.message || 'Bulk save failed' });
        return;
      }
      setSnack({ severity: 'success', message: 'Order, visibility & spotlight saved.' });
      setOrderDirty(false);
      await loadSections();
    } catch (e) {
      setSnack({ severity: 'error', message: e.response?.data?.message || e.message });
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Loksy Home Screen Controls
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            loksyhomescreen_controls — hide / display home categories (Govt Alerts, Real Estate,
            Food Zone…). Bottom nav & top-bar icons on other tabs. Section keys stay fixed in Flutter.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            color="primary"
            startIcon={syncingAll ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={() => syncAllWithCitizen()}
            disabled={loading || syncingAll || chromeSaving}
          >
            Sync all ↔ Citizen
          </Button>
          {pageTab === 'home' ? (
            <Button
              variant="outlined"
              startIcon={
                importingThumbs ? <CircularProgress size={16} /> : <ImageSearchIcon />
              }
              onClick={importAppThumbnails}
              disabled={loading || importingThumbs || !cmsReady || stats.missingCms === 0}
            >
              Sync covers
            </Button>
          ) : null}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (pageTab === 'fullscreen') loadPageBlocks();
              else if (pageTab === 'home') loadSections();
              else loadChrome();
            }}
            disabled={loading || syncingAll || chromeSaving || blocksSaving}
          >
            Refresh
          </Button>
          {pageTab === 'fullscreen' ? (
            <Button
              variant="contained"
              startIcon={blocksSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!blocksDirty || blocksSaving}
              onClick={savePageBlocks}
            >
              Save full screen
            </Button>
          ) : pageTab === 'home' ? (
            <Button
              variant="contained"
              startIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!orderDirty || bulkSaving}
              onClick={saveOrderVisibility}
            >
              Save order / visibility
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={chromeSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={!chromeDirty || chromeSaving}
              onClick={saveChrome}
            >
              Save nav / top bar
            </Button>
          )}
        </Stack>
      </Stack>

      {typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1') ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>API mismatch risk:</strong> Local admin saves to local API. Live Citizen app usually
          reads DigitalOcean — open admin on the live URL to push Profile/nav changes to the app.
          Use <strong>Sync all ↔ Citizen</strong> after Save.
        </Alert>
      ) : null}

      {pageTab === 'home' && orderDirty ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Order / spotlight changes are not live yet — click{' '}
          <strong>Save order / visibility</strong>. Eye hide/show already saves instantly.
        </Alert>
      ) : null}

      <Tabs
        value={pageTab}
        onChange={(_, v) => setPageTab(v)}
        sx={{ mb: 2, bgcolor: 'grey.100', borderRadius: 2, minHeight: 44 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="fullscreen" label="Full screen" />
        <Tab value="home" label="Home carousels" />
        <Tab value="bottom_nav" label="Bottom nav" />
        <Tab value="top_bar" label="Top bar icons" />
      </Tabs>

      {pageTab === 'fullscreen' ? (
        <Box mb={2}>
          <CitizenFullScreenBoard
            blocks={pageBlocks}
            onChange={(next) => {
              setPageBlocks(next);
              setBlocksDirty(true);
            }}
          />
        </Box>
      ) : null}

      {pageTab === 'bottom_nav' || pageTab === 'top_bar' ? (
        <Box mb={2}>
          {pageTab === 'bottom_nav' ? (
            <ShellChromeBoard
              title="Citizen bottom navigation"
              blurb="Home · MyTown · Town Reels · My Ads · Profile — drag to reorder or Off to hide."
              items={chrome.bottom_nav}
              addableKeys={chromeCatalog.bottom_nav}
              onChange={(next) => {
                setChrome((prev) => ({ ...prev, bottom_nav: next }));
                setChromeDirty(true);
              }}
            />
          ) : (
            <ShellChromeBoard
              title="Citizen top bar icons"
              blurb="My Orders · Feedback · Language — e.g. hide Feedback or move Language."
              items={chrome.top_bar}
              addableKeys={chromeCatalog.top_bar}
              onChange={(next) => {
                setChrome((prev) => ({ ...prev, top_bar: next }));
                setChromeDirty(true);
              }}
            />
          )}
        </Box>
      ) : null}

      {pageTab === 'home' ? (
        <>
      {!cmsReady && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          CMS columns not detected. Run migration <strong>129_citizen_home_screens_cms.sql</strong>{' '}
          then restart tsbackend. Order / hide still work; thumbnails & schedule need the migration.
        </Alert>
      )}

      <Alert severity="success" sx={{ mb: 2 }}>
        <strong>Live screens:</strong> Eye icon hide/show saves immediately to the API. Citizen home
        refreshes within ~30s (or pull-to-refresh / reopen). Use <em>Sync from Citizen app</em> to
        fill empty covers, then edit here.
      </Alert>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Locsy Spin:</strong> Digital pamphlets use <em>Viewed · Spin & Save</em> (
        <code>ad_engage</code>). Listing publish uses <code>listing_publish</code>. Configure in{' '}
        <a href="/content/lucky-wheel">Locsy Spin</a> — keep Share-with-Locsy templates in the pool.
      </Alert>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Cover image spec:</strong> {MITRA_CARD_WIDTH}×{MITRA_CARD_HEIGHT}px ({MITRA_CARD_ASPECT}{' '}
        landscape) · target ~{Math.round(MITRA_CARD_TARGET_BYTES / 1024)} KB · same auto-crop pipeline
        as Mitra Screens. Cards show CMS cover, else Locsy app default / demo art. Drag the handle
        (or focus it and use arrow keys) to reorder — then Save. Edit a section →{' '}
        <strong>Browse & pick</strong> to search existing app covers.
      </Alert>

      <TextField
        size="small"
        fullWidth
        placeholder="Search carousels by title, key, badge… (e.g. food, jobs, rentals)"
        value={pageSearch}
        onChange={(e) => setPageSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: pageSearch ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => setPageSearch('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {searchActive ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing {filteredSorted.length} of {sorted.length} sections. Clear search to drag-reorder
          the full grid.
        </Alert>
      ) : null}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Live on home
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {stats.visible}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Hidden
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {stats.hidden}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Scheduled
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {stats.scheduled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Spotlight
              </Typography>
              <Typography variant="body1" fontWeight={700} noWrap>
                {stats.spotlight?.title || '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} mb={1}>
              Jump to position
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Or drag cards below / use ↑↓ on a focused drag handle. Example: move Fresh Market to
              5th — pick section, set 5, Move, then Save.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                <InputLabel>Carousel</InputLabel>
                <Select
                  label="Carousel"
                  value={jumpId}
                  onChange={(e) => setJumpId(e.target.value)}
                >
                  {sorted.map((s, i) => (
                    <MenuItem key={s.id} value={s.id}>
                      #{i + 1} · {s.title} ({s.section_key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Position"
                value={jumpPos}
                onChange={(e) => setJumpPos(e.target.value)}
                inputProps={{ min: 1, max: Math.max(1, sorted.length) }}
                sx={{ width: 110 }}
              />
              <Button variant="outlined" startIcon={<VerticalAlignTopIcon />} onClick={jumpToPosition}>
                Move
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={800} mb={1}>
              Layout presets
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(LAYOUT_PRESETS).map(([key, p]) => (
                <Tooltip key={key} title={p.blurb}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => applyPreset(key)}
                  >
                    {p.label}
                  </Button>
                </Tooltip>
              ))}
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              height: '100%',
              bgcolor: '#0F172A',
              color: 'white',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <PhoneIphoneIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight={800}>
                Home feed preview
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.7 }} display="block" mb={1.5}>
              Same stacked carousels as Citizen home (visible only · spotlight on top)
            </Typography>
            <HomeFeedPreview sections={visibleSorted} />
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={8} textAlign="center">
          <CircularProgress />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={displayList.map((s) => s.id)}
            strategy={rectSortingStrategy}
          >
            <Grid container spacing={2}>
              {displayList.length === 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info">No carousels match “{pageSearch}”.</Alert>
                </Grid>
              ) : (
                displayList.map((section) => {
                  const index = sorted.findIndex((s) => s.id === section.id);
                  return (
                    <SortableSectionCard
                      key={section.id}
                      section={section}
                      index={index < 0 ? 0 : index}
                      total={sorted.length}
                      dragDisabled={searchActive}
                      onMove={moveSection}
                      onToggleVisible={toggleVisible}
                      onSpotlight={setSpotlight}
                      onEdit={openEdit}
                    />
                  );
                })
              )}
            </Grid>
          </SortableContext>
          <DragOverlay>
            {activeDragSection ? (
              <Card
                variant="outlined"
                sx={{
                  width: 260,
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 8,
                  opacity: 0.95,
                }}
              >
                <Box sx={{ bgcolor: '#0F172A', aspectRatio: '3 / 2' }}>
                  <SectionThumb section={activeDragSection} />
                </Box>
                <Box sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>
                    {activeDragSection.title}
                  </Typography>
                </Box>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
        </>
      ) : null}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit · {editing?.title || editing?.section_key}
          <Typography variant="caption" display="block" color="text.secondary">
            Key locked: {editing?.section_key} (Flutter routing depends on it)
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  bgcolor: '#0F172A',
                  borderRadius: 2,
                  aspectRatio: '3 / 2',
                  overflow: 'hidden',
                  mb: 1,
                }}
              >
                <SectionThumb
                  section={{
                    ...editing,
                    thumbnail_url: form.thumbnail_url,
                  }}
                />
              </Box>
              {!form.thumbnail_url && editing ? (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Showing Locsy app {resolveSectionCoverUrl(editing).source} art until you upload a
                  CMS cover.
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={
                    thumbUploading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <CloudUploadOutlinedIcon />
                    )
                  }
                  disabled={thumbUploading || !cmsReady}
                >
                  Upload cover
                  <input
                    ref={thumbInputRef}
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                  />
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CollectionsIcon />}
                  disabled={!cmsReady}
                  onClick={() => setCoverPickerOpen(true)}
                >
                  Browse & pick
                </Button>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  disabled={saving || !form.thumbnail_url}
                  onClick={handleResetThumb}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  fullWidth
                  required
                />
                <TextField
                  label="Subtitle / tagline"
                  value={form.subtitle}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <TextField
                  label="Badge (optional)"
                  value={form.badge}
                  onChange={(e) => setForm((f) => ({ ...f, badge: e.target.value.slice(0, 32) }))}
                  fullWidth
                  helperText="Shown next to section title in Citizen home"
                />
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {BADGE_PRESETS.map((b) => (
                    <Chip
                      key={b}
                      label={b}
                      size="small"
                      onClick={() => setForm((f) => ({ ...f, badge: b }))}
                      variant={form.badge === b ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_visible}
                      onChange={(e) => setForm((f) => ({ ...f, is_visible: e.target.checked }))}
                    />
                  }
                  label="Visible on Citizen home"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_spotlight}
                      onChange={(e) => setForm((f) => ({ ...f, is_spotlight: e.target.checked }))}
                    />
                  }
                  label="Spotlight (temporary #1 without changing saved order)"
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Show from"
                    type="datetime-local"
                    value={form.visible_from}
                    onChange={(e) => setForm((f) => ({ ...f, visible_from: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={!cmsReady}
                  />
                  <TextField
                    label="Show until"
                    type="datetime-local"
                    value={form.visible_until}
                    onChange={(e) => setForm((f) => ({ ...f, visible_until: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={!cmsReady}
                  />
                </Stack>
                <TextField
                  label="AI merchandising brief"
                  value={form.ai_brief}
                  onChange={(e) => setForm((f) => ({ ...f, ai_brief: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="e.g. Mango season — bright fruit stall, morning market energy"
                  disabled={!cmsReady}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={handleGeneratePrompt}
                    disabled={!cmsReady}
                  >
                    Generate prompt
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyPrompt}
                    disabled={!aiPrompt}
                  >
                    Copy
                  </Button>
                </Stack>
                {aiPrompt ? (
                  <TextField
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    label="AI prompt"
                  />
                ) : null}
                <TextField
                  label="Admin notes (internal)"
                  value={form.admin_notes}
                  onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!cmsReady}
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Save section
          </Button>
        </DialogActions>
      </Dialog>

      <CoverPickerDialog
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        sections={sorted}
        preferSectionKey={editing?.section_key}
        onPick={(item) => {
          setForm((f) => ({ ...f, thumbnail_url: item.url }));
          setCoverPickerOpen(false);
          setSnack({
            severity: 'success',
            message: `Picked “${item.title}” — click Save section to apply.`,
          });
        }}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
  );
};

export default CitizenScreens;
